import * as Sentry from "@sentry/react";
import type { DocumentData } from "firebase/firestore/lite";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import type { User } from "senf-shared/types";
import Cookies from "universal-cookie";

const cookies = new Cookies();

/**
 * Initialize Sentry for error tracking
 * This should be called only after user has accepted all cookies
 */
export const initSentry = () => {
  if (typeof window === "undefined" || Sentry.isInitialized()) {
    return;
  }

  // Check if user has accepted all cookies before initializing Sentry
  const consentSettings = cookies.get("consent_settings");
  if (consentSettings !== "all") {
    return;
  }

  const isProduction = import.meta.env.VITE_APP_MODE === "production";
  const isSentryEnabled = import.meta.env.VITE_ENABLE_SENTRY === "true";

  if (isProduction || isSentryEnabled) {
    const environment = import.meta.env.VITE_APP_MODE || "production";

    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      release: import.meta.env.VITE_APP_VERSION || "unknown-release",
      environment,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          networkDetailAllowUrls: [window.location.origin],
          networkCaptureBodies: true,
          networkRequestHeaders: ["Cache-Control"],
          networkResponseHeaders: ["Referrer-Policy"],
        }),
      ],
      // Performance monitoring: Set lower rate for production
      tracesSampleRate: 0.1,
      // Session Replay: Set lower rates for production
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0, // Capture replays for all errors
      debug: environment !== "production",
    });
  }
};
/**
 * Captures an exception safely, falling back to console.error if Sentry is not available
 * @param error The error to capture
 * @param context Additional context for the error
 */
export const captureException = (
  error: unknown,
  context?: Record<string, unknown>,
) => {
  if (typeof window !== "undefined" && window.Sentry) {
    window.Sentry.captureException(error);
  }
  console.error(context?.message || "An error occurred:", error, context);
};
/**
 * Set user information for better error tracking
 * Call this when user logs in or data changes
 * @param {(User & DocumentData) | null} user - User information or null if logged out
 */
export const setUser = (user: (User & DocumentData) | null) => {
  if (!Sentry.isInitialized()) {
    return;
  }

  if (user) {
    Sentry.setUser({
      id: user.userId,
      username: user.handle,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Set custom context to help with debugging
 * @param {string} name - Context name (e.g., 'projectDetails')
 * @param {Record<string, unknown> | null} data - Context data or null to clear
 */
export const setContext = (
  name: string,
  data: Record<string, unknown> | null,
) => {
  if (!Sentry.isInitialized()) {
    return;
  }

  Sentry.setContext(name, data);
};

/**
 * Set a tag for the current session
 * @param {string} key - Tag key (e.g., 'user_role')
 * @param {string | number | boolean} value - Tag value
 */
export const setTag = (key: string, value: string | number | boolean) => {
  if (!Sentry.isInitialized()) {
    return;
  }

  Sentry.setTag(key, value);
};

// Re-export Sentry for convenience
export * as Sentry from "@sentry/react";
